const NON_GAME_LOCKS = new Set(['Bye Week', 'No Pick']);

/**
 * Sort bucket for lock overview rows: live (0), upcoming (1), finished (2), non-game (3).
 * Mirrors weekly pick sheet kickoff sort (active → future → completed).
 */
export const getLockGameStatusOrder = (lock) => {
  if (!lock || NON_GAME_LOCKS.has(lock.lock) || !lock.kickoff) {
    return 3;
  }

  const { gameStatus, kickoff } = lock;
  if (gameStatus === 'in_progress') return 0;
  if (gameStatus === 'completed') return 2;
  if (gameStatus === 'scheduled') {
    return new Date(kickoff) > new Date() ? 1 : 2;
  }

  const now = new Date();
  const startTime = new Date(kickoff);
  const endTime = new Date(startTime);
  endTime.setHours(endTime.getHours() + 4);

  if (now >= startTime && now <= endTime) return 0;
  if (now < startTime) return 1;
  return 2;
};

export const sortLocksByGameSchedule = (locks) =>
  [...locks].sort((a, b) => {
    const aOrder = getLockGameStatusOrder(a);
    const bOrder = getLockGameStatusOrder(b);
    if (aOrder !== bOrder) return aOrder - bOrder;

    if (aOrder === 3) {
      return (a.username || '').localeCompare(b.username || '');
    }

    const aKick = a.kickoff ? new Date(a.kickoff).getTime() : 0;
    const bKick = b.kickoff ? new Date(b.kickoff).getTime() : 0;
    if (aKick !== bKick) return aKick - bKick;

    return (a.username || '').localeCompare(b.username || '');
  });

export const SIMPSONS_NAMES = [
  'Hugh Jass',
  'Amanda Hugginkiss',
  'Ivana Tinkle',
  'Seymour Butts',
  'Anita Bath',
  'Mike Rotch',
  'I.P. Freely',
  'Ben Dover',
  'Oliver Clothesoff',
  'Homer Sexual',
];

export const getSimpsonsName = (username, weekNumber) => {
  const seed = (username?.charCodeAt(0) || 0) + weekNumber;
  const index = seed % SIMPSONS_NAMES.length;
  return SIMPSONS_NAMES[index];
};

export const generateAllWeeks = () => {
  const weeks = [];

  for (let week = 1; week <= 15; week += 1) {
    weeks.push({
      week,
      week_display: `Week ${week}`,
      isBowlWeek: false,
      pickNumber: 1,
    });
  }

  for (let bowlWeek = 17; bowlWeek <= 18; bowlWeek += 1) {
    for (let pick = 1; pick <= 3; pick += 1) {
      weeks.push({
        week: bowlWeek,
        week_display: `BW${bowlWeek - 16}P${pick}`,
        isBowlWeek: true,
        pickNumber: pick,
      });
    }
  }

  return weeks;
};

export const getWeekOptionLabel = (week, calendarData = []) => {
  const calendarWeek = calendarData.find((cw) => cw.week === week);
  const isBowlWeek = calendarWeek?.season_type === 'postseason' && week >= 17;
  return isBowlWeek ? `BW${week - 16}` : `Week ${week}`;
};

export const isMetricEmpty = (lock) =>
  lock?.lock === 'Bye Week' || lock?.lock === 'No Pick' || lock?.lock === 'TBD';

export const mergeAndSortLocksForWeek = ({
  lockData,
  allActiveUsers,
  leaderboardData,
  selectedWeek,
  currentWeek,
  settings,
  hasReachedMaxPicks,
  getSimpsonsNameForWeek = getSimpsonsName,
}) => {
  if (!lockData?.locks?.length) {
    return lockData?.locks || [];
  }

  const mergedLocks = [...lockData.locks];
  const usersWithLocksForCurrentWeek = new Set(mergedLocks.map((lock) => lock.username));
  const usersInSeason = [...new Set(lockData.locks.map((lock) => lock.username))];
  const allActiveUsersArray = Array.isArray(allActiveUsers) ? allActiveUsers : [];

  allActiveUsersArray.forEach((username) => {
    if (!usersWithLocksForCurrentWeek.has(username) && usersInSeason.includes(username)) {
      mergedLocks.push({
        username,
        kickoff: null,
        matchup: null,
        lock: 'No Pick',
        score: null,
        total: null,
        margin: null,
        status: 'No Pick',
        leadTime: null,
        tv: null,
      });
    }
  });

  const shouldUseProperRanking =
    (settings.show_locks && settings.show_lockers) ||
    (!settings.show_locks && settings.show_lockers) ||
    hasReachedMaxPicks ||
    selectedWeek < currentWeek;

  if (!shouldUseProperRanking) {
    return mergedLocks.sort((a, b) => {
      const aDisplayName = getSimpsonsNameForWeek(a.username, selectedWeek);
      const bDisplayName = getSimpsonsNameForWeek(b.username, selectedWeek);
      return aDisplayName.localeCompare(bDisplayName);
    });
  }

  return sortLocksByGameSchedule(mergedLocks);
};

export const getByWeekVisibility = ({
  lock,
  selectedWeek,
  currentWeek,
  season,
  effectiveSeason,
  settings,
  hasReachedMaxPicks,
  hasUserLockedForWeek,
}) => {
  const isCurrentWeek = selectedWeek === currentWeek;
  const isFutureWeek = selectedWeek > currentWeek;
  const hasUserLockedForSelectedWeek = hasUserLockedForWeek(selectedWeek);

  const shouldBlurUser =
    !hasReachedMaxPicks &&
    !settings.show_lockers &&
    season === effectiveSeason &&
    (isCurrentWeek || (isFutureWeek && !hasUserLockedForSelectedWeek));

  const shouldBlurLockData =
    !hasReachedMaxPicks &&
    !settings.show_locks &&
    settings.show_lockers &&
    season === effectiveSeason &&
    isCurrentWeek &&
    lock?.lock &&
    lock.lock !== 'No Pick' &&
    lock.lock !== 'Bye Week';

  const shouldBlurEverything =
    !hasReachedMaxPicks &&
    !settings.show_locks &&
    !settings.show_lockers &&
    season === effectiveSeason &&
    isCurrentWeek;

  return {
    isCurrentWeek,
    isFutureWeek,
    shouldBlurUser,
    shouldBlurLockData,
    shouldBlurEverything,
  };
};

export const getByUserRowVisibility = ({
  weekNumber,
  currentWeek,
  season,
  effectiveSeason,
  settings,
  hasUserLockedForWeek,
}) => {
  const isCurrentWeek = weekNumber === currentWeek;
  const isFutureWeek = weekNumber > currentWeek;
  const shouldMaskRow =
    !hasUserLockedForWeek(weekNumber) &&
    !settings.show_lockers &&
    season === effectiveSeason &&
    (isCurrentWeek || isFutureWeek);

  const shouldBlurLockDetails =
    !hasUserLockedForWeek(weekNumber) &&
    !settings.show_locks &&
    settings.show_lockers &&
    season === effectiveSeason &&
    isCurrentWeek;

  const shouldBlurEverything =
    !hasUserLockedForWeek(weekNumber) &&
    !settings.show_locks &&
    !settings.show_lockers &&
    season === effectiveSeason &&
    isCurrentWeek;

  return { shouldMaskRow, shouldBlurLockDetails, shouldBlurEverything, isCurrentWeek, isFutureWeek };
};
